import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import API from "../../../api/api";
import {
  FaTrophy,
  FaDownload,
  FaSave,
  FaEdit,
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaFilter,
  FaRedoAlt,
  FaUserTag,
  FaRulerVertical,
  FaWeight,
  FaTshirt,
  FaShoePrints,
  FaBoxOpen,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { downloadHtmlAsPdf } from "../../../utils/pdfDownloadHelper";

const DRESS_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "Custom"];
const SHOE_SIZES = ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const STANDARD_KIT_ITEMS = [
  "Tracksuit / Dress",
  "Sport Shoes",
  "Water Bottle",
  "Kit Bag",
  "Cap",
  "Socks (2 Pairs)",
  "Towel",
];

const CASTE_CATEGORIES = [
  "OC",
  "BC-A",
  "BC-B",
  "BC-C",
  "BC-D",
  "BC-E",
  "SC",
  "ST",
  "Minority",
  "Other",
];

const KitMeasurements = ({ campId: propCampId, selectedCamp, activeCamp, isCoach: propIsCoach }) => {
  const { user } = useContext(AuthContext) || {};
  const isCoach = !!propIsCoach || user?.role === "coach";

  const [loading, setLoading] = useState(false);
  const [savingBatch, setSavingBatch] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [campId, setCampId] = useState(propCampId || selectedCamp?.id || activeCamp?.id || null);

  useEffect(() => {
    const cid = propCampId || selectedCamp?.id || activeCamp?.id;
    if (cid) {
      setCampId(cid);
    } else {
      const getActiveCampFn = isCoach ? API.camp.coachPortal.getActiveCamp : API.camp.getActiveCamp;
      getActiveCampFn().then((res) => {
        const id = res?.data?.camp?.id || res?.camp?.id;
        if (id) setCampId(id);
      }).catch(() => { });
    }
  }, [propCampId, selectedCamp, activeCamp, isCoach]);

  // Track inline edited changes: { [athleteId]: { height_cm, weight_kg, dress_size, shoe_size, kit_distribution_status, kit_remarks } }
  const [modifiedRows, setModifiedRows] = useState({});

  // Single edit modal state
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [editForm, setEditForm] = useState({
    height_cm: "",
    weight_kg: "",
    dress_size: "",
    shoe_size: "",
    caste: "",
    subcaste: "",
    kit_distribution_status: "pending",
    kit_items_issued: [],
    kit_remarks: "",
  });


  const fetchKits = useCallback(async (targetCampId) => {
    const id = targetCampId || campId || propCampId || selectedCamp?.id || activeCamp?.id;
    setLoading(true);
    try {
      const getKitsFn = isCoach ? API.camp.coachPortal.getKits : API.camp.getKits;
      const res = await getKitsFn({
        camp_id: id || undefined,
        team_name: selectedTeam !== "all" ? selectedTeam : undefined,
      });
      const data = res?.data || res || {};
      const athleteList = data.athletes || [];
      setAthletes(athleteList);

      // Extract unique team names as strings so React never receives an object child
      const rawTeams = Array.isArray(data.teams) ? data.teams : [];
      const extractedTeamNames = rawTeams
        .map((t) => (typeof t === "string" ? t : t?.team_name))
        .filter(Boolean);
      const athleteTeamNames = athleteList.map((a) => a.team_name).filter(Boolean);
      const uniqueTeams = Array.from(new Set([...extractedTeamNames, ...athleteTeamNames]));
      setTeams(uniqueTeams);
      setModifiedRows({});
    } catch (err) {
      console.error("Failed to load kit measurements:", err);
      Swal.fire({
        icon: "error",
        title: "Load Failed",
        text: err?.response?.data?.message || "Failed to fetch kit & measurement data.",
      });
    } finally {
      setLoading(false);
    }
  }, [campId, propCampId, selectedCamp, activeCamp, selectedTeam, isCoach]);

  useEffect(() => {
    fetchKits(campId);
  }, [fetchKits, campId]);

  // Filtered athletes
  const filteredAthletes = useMemo(() => {
    return athletes.filter((ath) => {
      if (selectedTeam !== "all" && ath.team_name !== selectedTeam) return false;
      if (selectedStatus !== "all" && (ath.kit_distribution_status || "pending") !== selectedStatus) {
        return false;
      }
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchesName = ath.name?.toLowerCase().includes(q);
        const matchesBib = ath.bib_no?.toLowerCase().includes(q);
        const matchesSport = ath.discipline_name?.toLowerCase().includes(q);
        const matchesDistrict = ath.district?.toLowerCase().includes(q);
        if (!matchesName && !matchesBib && !matchesSport && !matchesDistrict) return false;
      }
      return true;
    });
  }, [athletes, selectedTeam, selectedStatus, searchTerm]);

  // Metrics
  const metrics = useMemo(() => {
    const total = athletes.length;
    let measured = 0;
    let issued = 0;
    let partial = 0;
    let pending = 0;

    athletes.forEach((ath) => {
      const isMeasured = ath.height_cm || ath.weight_kg || ath.dress_size || ath.shoe_size;
      if (isMeasured) measured++;
      const st = ath.kit_distribution_status || "pending";
      if (st === "issued") issued++;
      else if (st === "partial") partial++;
      else pending++;
    });

    return { total, measured, issued, partial, pending };
  }, [athletes]);

  // Grouped by team for printing
  const groupedByTeam = useMemo(() => {
    const groups = {};
    const list = selectedTeam === "all" ? filteredAthletes : filteredAthletes.filter(a => a.team_name === selectedTeam);
    list.forEach((ath) => {
      const team = ath.team_name || "Unassigned / General Squad";
      if (!groups[team]) groups[team] = [];
      groups[team].push(ath);
    });
    return groups;
  }, [filteredAthletes, selectedTeam]);

  // Inline change handler
  const handleInlineChange = (athleteId, field, value) => {
    setModifiedRows((prev) => {
      const existing = prev[athleteId] || {};
      const original = athletes.find((a) => a.id === athleteId) || {};
      return {
        ...prev,
        [athleteId]: {
          id: athleteId,
          height_cm: original.height_cm ?? "",
          weight_kg: original.weight_kg ?? "",
          dress_size: original.dress_size ?? "",
          shoe_size: original.shoe_size ?? "",
          caste: original.caste ?? (original.player?.caste ?? ""),
          subcaste: original.subcaste ?? (original.player?.subcaste ?? ""),
          kit_distribution_status: original.kit_distribution_status ?? "pending",
          kit_remarks: original.kit_remarks ?? "",
          ...existing,
          [field]: value,
        },
      };
    });
  };

  // Save all inline changes
  const handleSaveBatch = async () => {
    const changes = Object.values(modifiedRows);
    if (changes.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Changes",
        text: "You haven't made any edits to save.",
      });
      return;
    }

    setSavingBatch(true);
    try {
      const bulkUpdateFn = isCoach ? API.camp.coachPortal.bulkUpdateKitMeasurements : API.camp.bulkUpdateKitMeasurements;
      await bulkUpdateFn({
        measurements: changes,
        athletes: changes,
      });
      Swal.fire({
        icon: "success",
        title: "Measurements Saved!",
        text: `Successfully updated measurements for ${changes.length} athletes.`,
        timer: 1800,
        showConfirmButton: false,
      });
      fetchKits(campId);
    } catch (err) {
      console.error("Batch save failed:", err);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err?.response?.data?.message || "Failed to update measurements.",
      });
    } finally {
      setSavingBatch(false);
    }
  };

  // Open single modal
  const handleOpenEdit = (athlete) => {
    setEditingAthlete(athlete);
    setEditForm({
      height_cm: athlete.height_cm || "",
      weight_kg: athlete.weight_kg || "",
      dress_size: athlete.dress_size || "",
      shoe_size: athlete.shoe_size || "",
      caste: athlete.caste || athlete.player?.caste || "",
      subcaste: athlete.subcaste || athlete.player?.subcaste || "",
      kit_distribution_status: athlete.kit_distribution_status || "pending",
      kit_items_issued: Array.isArray(athlete.kit_items_issued)
        ? athlete.kit_items_issued
        : [],
      kit_remarks: athlete.kit_remarks || "",
    });
  };

  // Toggle single kit item issued
  const toggleKitItem = (item) => {
    setEditForm((prev) => {
      const current = prev.kit_items_issued || [];
      const updated = current.includes(item)
        ? current.filter((i) => i !== item)
        : [...current, item];
      return { ...prev, kit_items_issued: updated };
    });
  };

  // Save single modal
  const handleSaveSingle = async (e) => {
    e.preventDefault();
    if (!editingAthlete) return;

    try {
      const updateSingleFn = isCoach ? API.camp.coachPortal.updateKitMeasurement : API.camp.updateKitMeasurement;
      await updateSingleFn({
        athlete_id: editingAthlete.id,
        id: editingAthlete.id,
        ...editForm,
      });
      Swal.fire({
        icon: "success",
        title: "Updated Successfully",
        timer: 1400,
        showConfirmButton: false,
      });
      setEditingAthlete(null);
      fetchKits(campId);
    } catch (err) {
      console.error("Single save failed:", err);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err?.response?.data?.message || "Could not save kit details.",
      });
    }
  };

  // Trigger Dedicated Print for Kit Measurements (Isolated from CoachingCampHub)
  const triggerPrint = (mode) => {
    const list = selectedTeam === "all" ? filteredAthletes : filteredAthletes.filter((a) => a.team_name === selectedTeam);
    if (!list || list.length === 0) {
      Swal.fire("Notice", "No athlete records found to print for the selected team filter.", "info");
      return;
    }

    const campTitle = selectedCamp?.name || activeCamp?.name || "Para Sports Coaching Camp";
    const reportTitle = mode === "empty"
      ? "ATHLETE KIT & PHYSICAL MEASUREMENT RECORD SHEET (TEAM-WISE FORMAT)"
      : "ATHLETE KIT DISTRIBUTION REGISTER & ACKNOWLEDGEMENT";
    const dateStr = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const teamEntries = Object.entries(groupedByTeam);
    if (teamEntries.length === 0) {
      Swal.fire("Notice", "No team records found to print.", "info");
      return;
    }

    let sheetsHtml = "";
    teamEntries.forEach(([teamName, teamAthletes]) => {
      let rowsHtml = "";
      teamAthletes.forEach((ath, idx) => {
        const heightVal = mode === "empty" ? "" : (ath.height_cm ? `${ath.height_cm} cm` : "—");
        const weightVal = mode === "empty" ? "" : (ath.weight_kg ? `${ath.weight_kg} kg` : "—");
        const dressVal = mode === "empty" ? "" : (ath.dress_size || "—");
        const shoeVal = mode === "empty" ? "" : (ath.shoe_size || "—");
        const itemsIssued = mode === "empty" ? "" : (
          Array.isArray(ath.kit_items_issued) && ath.kit_items_issued.length > 0
            ? ath.kit_items_issued.join(", ")
            : "Standard Kit"
        );
        const statusRemarks = mode === "empty"
          ? ""
          : `${(ath.kit_distribution_status || "PENDING").toUpperCase()}${ath.kit_remarks ? ` (${ath.kit_remarks})` : ""}`;

        rowsHtml += `
          <tr style="height: 36px; border-bottom: 1px solid #334155;">
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; font-weight: bold; width: 32px;">${idx + 1}</td>
            <td style="border: 1px solid #0f172a; padding: 4px 8px; text-align: left;">
              <div style="font-weight: 700; font-size: 11px; color: #0f172a;">${ath.name || "N/A"}</div>
              <div style="font-size: 9px; color: #64748b;">${ath.district || ath.state || "AP"}</div>
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; font-family: monospace; font-weight: 700; font-size: 11px;">
              ${ath.id_number || ath.bib_no || "—"}
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: left; font-size: 10px;">
              ${ath.discipline_name || "Para Sports"}
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; font-weight: 600; font-size: 11px;">
              ${heightVal}
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; font-weight: 600; font-size: 11px;">
              ${weightVal}
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; font-weight: 700; font-size: 11px;">
              ${dressVal}
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; font-weight: 700; font-size: 11px;">
              ${shoeVal}
            </td>
            ${mode === "recorded" ? `
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: left; font-size: 9px;">
              ${itemsIssued}
            </td>` : ""}
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: left; font-size: 9px;">
              ${statusRemarks}
            </td>
            <td style="border: 1px solid #0f172a; padding: 4px 6px; text-align: center; width: 90px;"></td>
          </tr>
        `;
      });

      sheetsHtml += `
        <div class="team-sheet" style="page-break-after: always; break-after: page; box-sizing: border-box; padding: 8mm; margin: 0; background: #ffffff; color: #000000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
          <!-- Official Header -->
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 8px; margin-bottom: 12px;">
            <img src="/images/ap_govt.png" alt="Govt of AP" style="height: 54px; width: auto; object-fit: contain;" />
            <div style="text-align: center; flex: 1; padding: 0 12px;">
              <h2 style="margin: 0; font-size: 15px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px; color: #0f172a;">
                Government of Andhra Pradesh &bull; Sports Authority
              </h2>
              <h3 style="margin: 2px 0 0 0; font-size: 13px; font-weight: 700; text-transform: uppercase; color: #0369a1;">
                Coaching Camp: ${campTitle}
              </h3>
              <div style="margin-top: 4px; display: inline-block; background: #0f172a; color: #ffffff; padding: 3px 12px; font-size: 10px; font-weight: 800; text-transform: uppercase; border-radius: 4px; letter-spacing: 0.5px;">
                ${reportTitle}
              </div>
            </div>
            <img src="/images/parasports.png" alt="Para Sports" style="height: 48px; width: auto; object-fit: contain;" />
          </div>

          <!-- Team Info Subheader -->
          <div style="display: flex; justify-content: space-between; align-items: center; background: #f1f5f9; border: 1.5px solid #94a3b8; border-radius: 6px; padding: 6px 12px; margin-bottom: 10px; font-size: 11px; font-weight: 700;">
            <div>
              <span style="color: #475569; text-transform: uppercase;">TEAM / SQUAD: </span>
              <span style="color: #0f172a; font-size: 12px; font-weight: 900;">${teamName}</span>
            </div>
            <div>
              <span style="color: #475569; text-transform: uppercase;">TOTAL ATHLETES: </span>
              <span style="color: #0f172a; font-size: 12px; font-weight: 900;">${teamAthletes.length}</span>
            </div>
            <div>
              <span style="color: #475569; text-transform: uppercase;">DATE: </span>
              <span style="color: #0f172a; font-size: 12px;">${dateStr}</span>
            </div>
          </div>

          <!-- Table -->
          <table style="width: 100%; border-collapse: collapse; border: 1.5px solid #0f172a; font-size: 10px;">
            <thead>
              <tr style="background: #e2e8f0; border-bottom: 1.5px solid #0f172a; text-align: center; font-weight: 800;">
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 30px;">#</th>
                <th style="border: 1px solid #0f172a; padding: 6px 6px; text-align: left; width: 160px;">Athlete Name</th>
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">ID Number</th>
                <th style="border: 1px solid #0f172a; padding: 6px 6px; text-align: left; width: 100px;">Discipline</th>
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Height</th>
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Weight</th>
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Dress Size</th>
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Shoe Size</th>
                ${mode === "recorded" ? `<th style="border: 1px solid #0f172a; padding: 6px 6px; text-align: left; width: 120px;">Items Issued</th>` : ""}
                <th style="border: 1px solid #0f172a; padding: 6px 6px; text-align: left; width: 120px;">
                  ${mode === "empty" ? "Medical / Remarks" : "Status & Remarks"}
                </th>
                <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 100px;">Athlete Sign</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <!-- Dual Coach & Official Signatures -->
          <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 36px; padding: 0 16px; font-size: 11px; font-weight: 700;">
            <div style="text-align: center;">
              <div style="width: 170px; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
              <div>Team Coach / Assistant Coach 1</div>
              <div style="font-size: 9px; font-weight: normal; color: #64748b;">Date & Signature</div>
            </div>
            <div style="text-align: center;">
              <div style="width: 170px; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
              <div>Assistant Coach 2</div>
              <div style="font-size: 9px; font-weight: normal; color: #64748b;">Date & Signature</div>
            </div>
            <div style="text-align: center;">
              <div style="width: 170px; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
              <div>Kit Store In-Charge</div>
              <div style="font-size: 9px; font-weight: normal; color: #64748b;">Date & Signature</div>
            </div>
            <div style="text-align: center;">
              <div style="width: 170px; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
              <div>Camp Official</div>
              <div style="font-size: 9px; font-weight: normal; color: #64748b;">Samagra Shiksha / SAAP</div>
            </div>
          </div>
        </div>
      `;
    });

    const fullHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${reportTitle} - ${campTitle}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 6mm;
            }
            * {
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #000000;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .team-sheet:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
          </style>
        </head>
        <body>
          ${sheetsHtml}
        </body>
      </html>
    `;

    const cleanCampName = (campTitle || "Coaching_Camp").replace(/[^a-zA-Z0-9_-]/g, "_");
    const filename =
      mode === "empty"
        ? `Kit_Measurement_Empty_Format_${cleanCampName}`
        : `Kit_Distribution_Register_${cleanCampName}`;

    downloadHtmlAsPdf(fullHtml, filename, { orientation: "landscape" });
  };

  return (
    <div className="space-y-6">
      {/* Header Title Card */}
      <div className="bg-gradient-to-r from-cyan-900 via-sky-800 to-teal-900 text-white p-6 rounded-3xl shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold text-cyan-200 mb-2">
            <FaTrophy className="text-amber-400" />
            <span>Camp Logistics & Athlete Equipment</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black tracking-tight">
            Kit Measurements & Distribution
          </h2>
          <p className="text-xs md:text-sm text-cyan-100/80 mt-1 max-w-2xl">
            Track athlete physical parameters (Height, Weight, Shoe Size, Dress Size), monitor kit issuance status, and export official empty or recorded team-wise PDF sheets.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2.5 items-center">
          <button
            onClick={() => triggerPrint("empty")}
            className="inline-flex items-center gap-2 bg-white text-slate-800 hover:bg-slate-100 px-4 py-2.5 rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer"
            title="Download blank team-wise measurement format as PDF"
          >
            <FaDownload className="text-cyan-700" />
            <span>Download Empty Format (PDF)</span>
          </button>

          <button
            onClick={() => triggerPrint("recorded")}
            className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer"
            title="Download recorded kit distribution register as PDF"
          >
            <FaDownload />
            <span>Download Distribution (PDF)</span>
          </button>

          {Object.keys(modifiedRows).length > 0 && (
            <button
              onClick={handleSaveBatch}
              disabled={savingBatch}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-lg hover:shadow-xl transition-all animate-pulse"
            >
              <FaSave />
              <span>
                {savingBatch
                  ? "Saving..."
                  : `Save All (${Object.keys(modifiedRows).length})`}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                Total Athletes
              </span>
              <h3 className="text-2xl font-black text-slate-900 mt-0.5">
                {metrics.total}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center text-lg">
              <FaUserTag />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                Measured
              </span>
              <h3 className="text-2xl font-black text-indigo-600 mt-0.5">
                {metrics.measured}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-lg">
              <FaRulerVertical />
            </div>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {metrics.total ? Math.round((metrics.measured / metrics.total) * 100) : 0}% recorded
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                Fully Issued
              </span>
              <h3 className="text-2xl font-black text-emerald-600 mt-0.5">
                {metrics.issued}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg">
              <FaCheckCircle />
            </div>
          </div>
          <div className="text-[10px] text-emerald-600 font-medium mt-1">
            Kit received & verified
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                Partial Issued
              </span>
              <h3 className="text-2xl font-black text-amber-600 mt-0.5">
                {metrics.partial}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-lg">
              <FaExclamationCircle />
            </div>
          </div>
          <div className="text-[10px] text-amber-600 font-medium mt-1">
            Few items pending
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                Pending Kit
              </span>
              <h3 className="text-2xl font-black text-rose-600 mt-0.5">
                {metrics.pending}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-lg">
              <FaClock />
            </div>
          </div>
          <div className="text-[10px] text-rose-500 font-medium mt-1">
            Awaiting stock / delivery
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 md:w-64">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search athlete, bib, sport..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          {/* Team Filter */}
          <div className="flex items-center gap-1.5">
            <FaFilter className="text-slate-400 text-xs" />
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Teams ({teams.length})</option>
              {teams.map((t) => {
                const name = typeof t === "string" ? t : t?.team_name || "General Squad";
                return (
                  <option key={name} value={name}>
                    Team: {name}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">All Kit Statuses</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="issued">Fully Issued</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchKits}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-cyan-700 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-xl transition"
          >
            <FaRedoAlt className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Athletes Table with In-Place Editing */}
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex justify-between items-center">
          <div className="text-xs font-bold text-slate-700">
            Showing <span className="text-cyan-700 font-extrabold">{filteredAthletes.length}</span> Athletes
            {Object.keys(modifiedRows).length > 0 && (
              <span className="ml-3 text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full text-[11px] font-bold border border-amber-200">
                {Object.keys(modifiedRows).length} unsaved changes
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 italic">
            Tip: You can edit measurements directly in the table, then click "Save All".
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200 uppercase tracking-wider text-[10px]">
                <th className="py-3 px-3 w-10 text-center">#</th>
                <th className="py-3 px-3">Athlete</th>
                <th className="py-3 px-3">Team / Sport</th>
                <th className="py-3 px-2 w-28">Caste / Subcaste</th>
                <th className="py-3 px-2 w-24">Height (cm)</th>
                <th className="py-3 px-2 w-24">Weight (kg)</th>
                <th className="py-3 px-2 w-28">Dress Size</th>
                <th className="py-3 px-2 w-24">Shoe Size</th>
                <th className="py-3 px-3 w-32">Kit Status</th>
                <th className="py-3 px-3">Remarks / Items</th>
                <th className="py-3 px-3 w-20 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="11" className="py-12 text-center text-slate-400">
                    Loading kit & measurement records...
                  </td>
                </tr>
              ) : filteredAthletes.length === 0 ? (
                <tr>
                  <td colSpan="11" className="py-12 text-center text-slate-400">
                    No athletes found matching the filters.
                  </td>
                </tr>
              ) : (
                filteredAthletes.map((ath, index) => {
                  const rowChanges = modifiedRows[ath.id] || {};
                  const curHeight = rowChanges.height_cm !== undefined ? rowChanges.height_cm : (ath.height_cm || "");
                  const curWeight = rowChanges.weight_kg !== undefined ? rowChanges.weight_kg : (ath.weight_kg || "");
                  const curDress = rowChanges.dress_size !== undefined ? rowChanges.dress_size : (ath.dress_size || "");
                  const curShoe = rowChanges.shoe_size !== undefined ? rowChanges.shoe_size : (ath.shoe_size || "");
                  const curCaste = rowChanges.caste !== undefined ? rowChanges.caste : (ath.caste || ath.player?.caste || "");
                  const curSubcaste = rowChanges.subcaste !== undefined ? rowChanges.subcaste : (ath.subcaste || ath.player?.subcaste || "");
                  const curStatus = rowChanges.kit_distribution_status !== undefined ? rowChanges.kit_distribution_status : (ath.kit_distribution_status || "pending");
                  const isDirty = Boolean(modifiedRows[ath.id]);

                  return (
                    <tr
                      key={ath.id}
                      className={`hover:bg-slate-50/80 transition-colors ${isDirty ? "bg-amber-50/40" : ""
                        }`}
                    >
                      <td className="py-3 px-3 text-center text-slate-400 font-mono text-[11px]">
                        {index + 1}
                      </td>
                      <td className="py-3 px-3">
                        <div className="font-bold text-slate-900">{ath.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          Bib: {ath.bib_no || "—"} &bull; {ath.district || "AP"}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px] font-bold">
                          {ath.team_name || "General"}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {ath.discipline_name || "Para Sports"}
                        </div>
                      </td>

                      {/* Caste / Subcaste */}
                      <td className="py-2 px-2">
                        <div className="flex flex-col gap-1">
                          <select
                            value={curCaste}
                            onChange={(e) =>
                              handleInlineChange(ath.id, "caste", e.target.value)
                            }
                            className="w-full bg-white border border-slate-300 rounded px-1.5 py-0.5 text-[10px] font-bold text-slate-800 focus:ring-1 focus:ring-cyan-500"
                          >
                            <option value="">Caste/Cat</option>
                            {CASTE_CATEGORIES.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={curSubcaste}
                            onChange={(e) =>
                              handleInlineChange(ath.id, "subcaste", e.target.value)
                            }
                            placeholder="Subcaste"
                            className="w-full bg-white border border-slate-300 rounded px-1.5 py-0.5 text-[10px] font-medium text-slate-800 focus:ring-1 focus:ring-cyan-500"
                          />
                        </div>
                      </td>

                      {/* Height (cm) */}
                      <td className="py-2 px-2">
                        <input
                          type="number"
                          step="0.1"
                          value={curHeight}
                          onChange={(e) =>
                            handleInlineChange(ath.id, "height_cm", e.target.value)
                          }
                          placeholder="cm"
                          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                        />
                      </td>

                      {/* Weight (kg) */}
                      <td className="py-2 px-2">
                        <input
                          type="number"
                          step="0.1"
                          value={curWeight}
                          onChange={(e) =>
                            handleInlineChange(ath.id, "weight_kg", e.target.value)
                          }
                          placeholder="kg"
                          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                        />
                      </td>

                      {/* Dress Size */}
                      <td className="py-2 px-2">
                        <select
                          value={curDress}
                          onChange={(e) =>
                            handleInlineChange(ath.id, "dress_size", e.target.value)
                          }
                          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Select</option>
                          {DRESS_SIZES.map((sz) => (
                            <option key={sz} value={sz}>
                              {sz}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Shoe Size */}
                      <td className="py-2 px-2">
                        <select
                          value={curShoe}
                          onChange={(e) =>
                            handleInlineChange(ath.id, "shoe_size", e.target.value)
                          }
                          className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Select</option>
                          {SHOE_SIZES.map((sz) => (
                            <option key={sz} value={sz}>
                              UK {sz}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Kit Distribution Status */}
                      <td className="py-2 px-2">
                        <select
                          value={curStatus}
                          onChange={(e) =>
                            handleInlineChange(
                              ath.id,
                              "kit_distribution_status",
                              e.target.value
                            )
                          }
                          className={`w-full border rounded-lg px-2 py-1 text-[11px] font-bold focus:ring-2 focus:ring-cyan-500 ${curStatus === "issued"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                            : curStatus === "partial"
                              ? "bg-amber-50 text-amber-700 border-amber-300"
                              : "bg-rose-50 text-rose-700 border-rose-300"
                            }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="partial">Partial</option>
                          <option value="issued">Issued</option>
                        </select>
                      </td>

                      {/* Remarks */}
                      <td className="py-3 px-3">
                        <div className="text-[11px] text-slate-600 truncate max-w-xs">
                          {Array.isArray(ath.kit_items_issued) && ath.kit_items_issued.length > 0 ? (
                            <span className="font-semibold text-cyan-800">
                              {ath.kit_items_issued.length} items issued
                            </span>
                          ) : (
                            <span className="text-slate-400">None marked</span>
                          )}
                          {ath.kit_remarks && (
                            <span className="ml-1 text-slate-500 italic">
                              &bull; {ath.kit_remarks}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-3 text-center">
                        <button
                          onClick={() => handleOpenEdit(ath)}
                          title="Detailed Edit & Items Issued"
                          className="p-1.5 text-slate-500 hover:text-cyan-700 hover:bg-slate-100 rounded-lg transition"
                        >
                          <FaEdit className="text-sm" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =========================================================
          DETAILED EDIT MODAL (SINGLE ATHLETE)
      ========================================================= */}
      {editingAthlete && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-cyan-800 to-teal-800 text-white p-5 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-black">
                Edit Kit & Measurements
              </h3>
              <p className="text-xs text-cyan-100">
                {editingAthlete.name} ({editingAthlete.bib_no || "No Bib"}) &bull; Team: {editingAthlete.team_name || "General"}
              </p>
            </div>
            <button
              onClick={() => setEditingAthlete(null)}
              className="text-white/80 hover:text-white p-1 rounded-lg"
            >
              ✕
            </button>
          </div>

          {/* Modal Form */}
          <form onSubmit={handleSaveSingle} className="p-6 space-y-4">
            {/* Caste / Category & Subcaste */}
            <div className="grid grid-cols-2 gap-4 bg-amber-50/50 p-3 rounded-2xl border border-amber-200">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Caste / Category
                </label>
                <select
                  value={editForm.caste}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, caste: e.target.value }))
                  }
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select Category</option>
                  {CASTE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Subcaste
                </label>
                <input
                  type="text"
                  value={editForm.subcaste}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, subcaste: e.target.value }))
                  }
                  placeholder="e.g. Kapu / Madiga / etc."
                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Height */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  <FaRulerVertical className="inline text-cyan-600 mr-1" />
                  Height (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={editForm.height_cm}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, height_cm: e.target.value }))
                  }
                  placeholder="e.g. 165.5"
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Weight */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  <FaWeight className="inline text-cyan-600 mr-1" />
                  Weight (kg)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={editForm.weight_kg}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, weight_kg: e.target.value }))
                  }
                  placeholder="e.g. 58.2"
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Dress Size */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  <FaTshirt className="inline text-cyan-600 mr-1" />
                  T-Shirt / Dress Size
                </label>
                <select
                  value={editForm.dress_size}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, dress_size: e.target.value }))
                  }
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select Size</option>
                  {DRESS_SIZES.map((sz) => (
                    <option key={sz} value={sz}>
                      {sz}
                    </option>
                  ))}
                </select>
              </div>

              {/* Shoe Size */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  <FaShoePrints className="inline text-cyan-600 mr-1" />
                  Shoe Size (UK)
                </label>
                <select
                  value={editForm.shoe_size}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, shoe_size: e.target.value }))
                  }
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">Select Size</option>
                  {SHOE_SIZES.map((sz) => (
                    <option key={sz} value={sz}>
                      UK {sz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Kit Items Issued Checkboxes */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-2">
                <FaBoxOpen className="inline text-cyan-600 mr-1" />
                Kit Items Handed Over to Athlete:
              </label>
              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-2xl border border-slate-200">
                {STANDARD_KIT_ITEMS.map((item) => {
                  const checked = (editForm.kit_items_issued || []).includes(item);
                  return (
                    <label
                      key={item}
                      className={`flex items-center gap-2 p-2 rounded-xl border text-xs cursor-pointer transition ${checked
                        ? "bg-cyan-50 border-cyan-400 text-cyan-900 font-bold"
                        : "bg-white border-slate-200 text-slate-600"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleKitItem(item)}
                        className="rounded text-cyan-600 focus:ring-cyan-500"
                      />
                      <span>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Distribution Status
              </label>
              <select
                value={editForm.kit_distribution_status}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    kit_distribution_status: e.target.value,
                  }))
                }
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
              >
                <option value="pending">Pending Distribution</option>
                <option value="partial">Partially Issued (Some Items Pending)</option>
                <option value="issued">Fully Issued & Handed Over</option>
              </select>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Special Kit Requirements / Medical Notes / Remarks
              </label>
              <textarea
                rows="2"
                value={editForm.kit_remarks}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, kit_remarks: e.target.value }))
                }
                placeholder="e.g. Left foot orthotic shoe required, extra socks provided..."
                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-cyan-500"
              ></textarea>
            </div>

            {/* Form Buttons */}
            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setEditingAthlete(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 rounded-xl text-xs font-bold bg-cyan-700 hover:bg-cyan-600 text-white shadow-md hover:shadow-lg"
              >
                Save Athlete Kit
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </div>
);
};

export { KitMeasurements };
export default KitMeasurements;
